/**
 * Buildings that have been strafed and are alight — AD-018.
 *
 * A port extension, and the module note says so up front: the original ignores
 * small-arms fire on `.WLD` scenery entirely. `collide.c` sweeps rounds against
 * *objects* — aeroplanes, gasbags, the class-5 train — and a placed cottage is
 * none of those; `.GI` carries a footprint and a roof height for every one of
 * them and no destructible flag anywhere (docs/formats/gi-format.md §3,
 * `flight/bomb-tunables.ts` says the same from the bombing side). Setting a
 * village alight is something this port adds. What it does *not* add is a
 * consequence: nothing here is scored, briefed or decided by.
 *
 * ## Why this is not an `EncounterTarget`
 *
 * `searchlight-posts.ts` made two lamps per battery into full targets and that
 * was right — a night mission places at most a dozen. Scenery is a different
 * size of problem. A region places **162 to 235** structures (measured across
 * all six baked regions; `scenery-fires.test.ts` re-runs the census), and every
 * `EncounterTarget` is thirteen hashed fields carried through every tick of
 * every mission for ever. Two hundred of them would put ~2 600 numbers into the
 * fingerprint to describe a state that is, at any instant of any sortie anyone
 * will ever fly, *empty* — and would put two hundred spheres into every
 * shooter's `gun-pass.ts` collision list, on every tick, to answer a question
 * that is almost always "no".
 *
 * So the state is **sparse in the fires, not dense in the buildings**: one
 * record per structure that has actually been shot, at most
 * `MAX_SCENERY_FIRES` of them, and nothing at all for the other two hundred.
 * A pristine region costs an empty array. The identity a record is keyed by is
 * the structure's index in its region's own placement list — an integer, stable
 * across loads because the baked `placements` array is ordered, and the same
 * identity the `/assets` World viewer already selects by.
 *
 * ## Why the hit test is injected and not imported
 *
 * `packages/engine` has no access to `.WLD` placements and must not gain any:
 * the region body is fetched by the web app and `@red-opus/baker` is Node-only.
 * `SceneryQuery` below is the same seam `flight/ground.ts`'s `GroundQuery` is,
 * for the same reason and with the same contract — a **pure function of world
 * geometry**, never snapshotted, never drawing randomness, so a state hash
 * stays a function of the flight (AD-007a). The default is `NO_SCENERY`, which
 * is what every test, every replay probe and every caller that predates this
 * module gets, and under it nothing in this file ever runs.
 */

import type { Vec3 } from "../flight/math3d.ts";
import type { AmmunitionType } from "./target-kinds.ts";

/**
 * One structure, as the query answers for it — an upright box on the ground.
 *
 * A box rather than a sphere because that is the shape the data already is.
 * `.GI` gives every placed object an axis-aligned half-extent pair and a roof
 * height, and the original's own `giTestObjectAt` tests that box **unrotated**,
 * ignoring the placement's `.WLD` heading (docs/formats/gi-format.md §5). Using
 * anything else here would mean a round passing the ground-collision test and
 * failing the fire test on the same wall.
 */
export interface SceneryStructure {
  /** Index into the region's own `placements` array. Stable across loads; see the module note. */
  readonly key: number;
  /** Footprint centre, world metres. */
  readonly x: number;
  readonly z: number;
  /** Ground level under the footprint, world metres — the box's floor. */
  readonly baseY: number;
  /** Half-extents on the two horizontal axes, world metres (`.GI` `extentXM`/`extentYM`). */
  readonly halfXM: number;
  readonly halfZM: number;
  /** Roof height above `baseY`, world metres — the tallest `.GI` facet the object has. */
  readonly roofM: number;
}

/**
 * What is standing in the world that a round can go into. Injected, exactly as
 * `GroundQuery` is; see the module note.
 */
export interface SceneryQuery {
  /**
   * The structure the segment `from → to` first enters, or `null` for a round
   * that hit nothing. Implementations must be pure and deterministic — the same
   * segment always answers the same way.
   *
   * `padM` grows every box by that many metres on all six faces before the
   * test — the Minkowski sum of the box and a sphere of that radius, read
   * conservatively. A **round** passes nothing and gets the plain box, which is
   * what keeps this module's answers identical to the day it was written; an
   * **aeroplane** passes its own `airframeCollisionRadiusM`, so that
   * `building-strike.ts` (AD-020) can ask "did the airframe go into this" with
   * the same hull AD-017 rams other aeroplanes with. A zero-length segment is a
   * point-in-box test, which AD-020 relies on.
   */
  structureOnSegment(from: Vec3, to: Vec3, padM?: number): SceneryStructure | null;
}

/** No buildings anywhere: the contract's default, and what every pre-AD-018 caller gets. */
export const NO_SCENERY: SceneryQuery = { structureOnSegment: () => null };

/**
 * One structure that has been shot, with what has happened to it.
 *
 * Nine numbers. The three that describe *where* — `x`, `y`, `z` — plus the two
 * that describe *how big the fire is* are carried on the record rather than
 * re-queried by the renderer, because the renderer has no key→structure map and
 * building one would mean shipping the placement list a second time. Five
 * numbers on at most six records is a cheaper duplication than the map.
 */
export interface SceneryFire {
  /** The struck structure's placement index (`SceneryStructure.key`). */
  readonly key: number;
  /** Where the fire is anchored: the footprint centre at **roof height**. */
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** Half the footprint's larger horizontal extent — how wide the flame front is. */
  readonly spreadM: number;
  /**
   * Hits accumulated, weighted by ammunition (`SCENERY_TUNABLES.hitWeight`).
   * Fractional, so an incendiary belt counts for more without a second counter.
   */
  readonly hits: number;
  /** Seconds since it caught. Zero until it does. */
  readonly burnSeconds: number;
  /** Seconds since the last round went into it — the kindling clock. */
  readonly sinceHitSeconds: number;
}

/**
 * Every number the behaviour is made of, in one register — the shape
 * `target-profiles.ts` and `particle-effects.ts` both use. **All
 * CALIBRATION-PENDING**: there is no `.DAT` field for any of them, because
 * there is no such mechanic in the original to have one.
 */
export const SCENERY_TUNABLES = {
  /**
   * Weighted hits before a structure catches. Six ball rounds into a farmhouse
   * is about a second of one Vickers on target — enough that a stray round from
   * a deflection shot at an aeroplane does not light the countryside, little
   * enough that a deliberate pass does.
   */
  igniteHits: 6,
  /**
   * What one round is worth, by belt. The 2:1 split is the same ratio
   * `target-profiles.ts` gives the balloon (0.09 ball against 0.14 incendiary is
   * 1.6:1) rounded up for a thatched roof, which is what Buckingham rounds were
   * *for*: they were adopted to set fire to things, and a shed is easier than a
   * hydrogen bag.
   */
  hitWeight: { regular: 1, incendiary: 2 } as Readonly<Record<AmmunitionType, number>>,
  /**
   * Seconds a struck-but-unlit structure remembers the rounds that went into
   * it. Past this the record is dropped and the count starts again — a pass
   * that put four rounds into a barn and flew on has not left it primed for the
   * rest of the sortie.
   */
  kindlingSeconds: 6,
  /**
   * Seconds a fire burns before it is out. Long enough to fly a circuit and
   * find it still going, short enough that a mission does not end with the
   * whole map alight — the "burns out rather than persists for ever" rule this
   * feature was asked to obey.
   */
  burnSeconds: 40,
} as const;

/**
 * Fires tracked at once, and the one number here that is **not** invented:
 * `smoke.c` allocates `SMOKE.DAT[7]` = 4 simultaneous trails and
 * `smoke-trail.ts` already honours that ceiling for aeroplanes. Six is that
 * four plus the two the port's own particle path has headroom for, and it is a
 * cap on *records*, so a burst that clips three barns without lighting any of
 * them can still hold slots for `kindlingSeconds`.
 *
 * When the list is full a new structure is simply not recorded. That is a
 * deliberate refusal rather than an eviction: eviction needs a policy, a policy
 * is a thing that can be got wrong, and the failure it would prevent is "the
 * seventh building you shot in forty seconds did not catch".
 */
export const MAX_SCENERY_FIRES = 6;

/** True once it has taken enough to catch. Derived, so `hits` is the only thing that has to be right. */
export function sceneryAlight(fire: SceneryFire): boolean {
  return fire.hits >= SCENERY_TUNABLES.igniteHits;
}

/** A fresh record for a structure taking its first round. */
function kindle(structure: SceneryStructure, weight: number): SceneryFire {
  return {
    key: structure.key,
    x: structure.x,
    y: structure.baseY + structure.roofM,
    z: structure.z,
    spreadM: Math.max(structure.halfXM, structure.halfZM),
    hits: weight,
    burnSeconds: 0,
    sinceHitSeconds: 0,
  };
}

/** One round going into one structure. Returns the list unchanged when the cap refuses it. */
export function strikeScenery(
  fires: readonly SceneryFire[],
  structure: SceneryStructure,
  ammunition: AmmunitionType,
): readonly SceneryFire[] {
  const weight = SCENERY_TUNABLES.hitWeight[ammunition];
  const index = fires.findIndex((fire) => fire.key === structure.key);
  if (index < 0) {
    if (fires.length >= MAX_SCENERY_FIRES) return fires;
    return [...fires, kindle(structure, weight)];
  }
  const existing = fires[index];
  if (!existing) return fires;
  // A round into something already alight does not make it burn longer. The
  // clock is the fire's, not the shooter's, or a pilot with ammunition to spare
  // could keep one building burning for the whole sortie.
  const struck: SceneryFire = sceneryAlight(existing)
    ? { ...existing, sinceHitSeconds: 0 }
    : { ...existing, hits: existing.hits + weight, sinceHitSeconds: 0 };
  return fires.map((fire, at) => (at === index ? struck : fire));
}

/**
 * An aeroplane going into a structure, which lights it outright — AD-020.
 *
 * No accumulation and no roll, on AD-017's precedent: `damage.c`'s ignition
 * ladder gives a kind-6 blow `ds:0x1046[6] = 500` against a threshold of 200,
 * *"so flying into a gasbag lights it — not probably, always"*
 * (`collisions-design.md` §3.3). A barn is not on that ladder because a barn is
 * not an object, but the reasoning transfers exactly: six weighted rifle rounds
 * is what `igniteHits` is a count of, and a burning aeroplane arriving through
 * the roof is not six rifle rounds. So the counter is set to the threshold
 * rather than nudged towards it.
 *
 * Refuses the same way `strikeScenery` does when the cap is full, and leaves an
 * already-alight structure's clock alone for the same reason it does.
 */
export function igniteScenery(
  fires: readonly SceneryFire[],
  structure: SceneryStructure,
): readonly SceneryFire[] {
  const index = fires.findIndex((fire) => fire.key === structure.key);
  if (index < 0) {
    if (fires.length >= MAX_SCENERY_FIRES) return fires;
    return [...fires, kindle(structure, SCENERY_TUNABLES.igniteHits)];
  }
  const existing = fires[index];
  if (!existing) return fires;
  const lit: SceneryFire = sceneryAlight(existing)
    ? { ...existing, sinceHitSeconds: 0 }
    : { ...existing, hits: SCENERY_TUNABLES.igniteHits, sinceHitSeconds: 0 };
  return fires.map((fire, at) => (at === index ? lit : fire));
}

/**
 * One tick of every fire: the alight ones age towards burning out, the
 * smouldering ones age towards being forgotten, and both are dropped when they
 * get there.
 *
 * Returns the *same array reference* when nothing changed and nothing is
 * burning, so the common case — an empty list, which is every tick of almost
 * every sortie — allocates nothing at all.
 */
export function advanceSceneryFires(fires: readonly SceneryFire[], dt: number): readonly SceneryFire[] {
  if (fires.length === 0) return fires;
  const next: SceneryFire[] = [];
  for (const fire of fires) {
    const sinceHitSeconds = fire.sinceHitSeconds + dt;
    if (!sceneryAlight(fire)) {
      if (sinceHitSeconds > SCENERY_TUNABLES.kindlingSeconds) continue;
      next.push({ ...fire, sinceHitSeconds });
      continue;
    }
    const burnSeconds = fire.burnSeconds + dt;
    if (burnSeconds > SCENERY_TUNABLES.burnSeconds) continue;
    next.push({ ...fire, burnSeconds, sinceHitSeconds });
  }
  return next;
}
